import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';

import { RouteFacade } from '@mitsubishi/core';
import { Study } from '@mitsubishi/shared/data-access';

import { StudyService } from '../services/study.service';
import { ProjectFacade } from '../state/project/project.facade';
import { StudyFacade } from '../state/study/study.facade';

interface ResolverDependencies {
  routeFacade: RouteFacade;
  projectFacade: ProjectFacade;
  studyFacade: StudyFacade;
  studyService: StudyService;
}

const resolveStudy = (studyId: string, projectId: string, deps: ResolverDependencies) => {
  const existingStudy = deps.studyFacade.getStudyById(studyId);

  if (existingStudy) {
    return existingStudy;
  }

  return deps.studyService.getTemporaryStudyDetail(projectId, studyId).pipe(
    tap(study => deps.projectFacade.addStudyToProject(study)),
    catchError(() => of(undefined))
  );
};

/**
 * Resolver that ensures the interim study is present in the projects state.
 * If not found in the state, it loads the study using a dedicated API request.
 */
export const interimStudyResolver: ResolveFn<Study | undefined> = (route, state) => {
  const deps: ResolverDependencies = {
    routeFacade: inject(RouteFacade),
    projectFacade: inject(ProjectFacade),
    studyFacade: inject(StudyFacade),
    studyService: inject(StudyService),
  };

  const { projectId, interimStudyId, studyId } = deps.routeFacade;

  if (!projectId || (!interimStudyId && !studyId)) {
    return undefined;
  }

  // If interimStudyId is available, use it; otherwise fall back to studyId
  const targetStudyId = interimStudyId || studyId;

  return resolveStudy(targetStudyId, projectId, deps);
};
